'use client'

import { Button } from '@/components/ui/shadcn/button'
import { Switch } from '@/components/ui/shadcn/switch'
import { Label } from '@/components/ui/shadcn/label'
import { Separator } from '@/components/ui/shadcn/separator'
import { Alert, AlertDescription } from '@/components/ui/shadcn/alert'
import { CheckCircle2, AlertCircle } from 'lucide-react'
import { AuthContext } from '@/contexts/AuthContext'
import { useState, useContext, useEffect } from 'react'
import axios from '@/lib/api/axios'

export default function EventRecording() {
  const authContext = useContext(AuthContext)
  const orgId = authContext.user?.organization?.id

  const [settings, setSettings] = useState({
    enabled: true,
    forceOrgSettings: false,
    meetingEvents: true,
    slackEvents: true,
    integrationEvents: true,
  })
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    const orgSettings = authContext.user?.organization?.eventRecordingSettings
    if (orgSettings) {
      setSettings({
        enabled: orgSettings.enabled ?? true,
        forceOrgSettings: orgSettings.forceOrgSettings ?? false,
        meetingEvents: orgSettings.meetingEvents ?? true,
        slackEvents: orgSettings.slackEvents ?? true,
        integrationEvents: orgSettings.integrationEvents ?? true,
      })
    }
  }, [authContext.user?.organization?.eventRecordingSettings])

  const handleSave = async () => {
    setLoading(true)
    setError(null)
    try {
      const { data } = await axios(
        `/api/admin/organization/${orgId}/settings/event-recording`,
        {
          method: 'PUT',
          data: settings,
        },
        authContext,
      )

      authContext.setuser(
        {
          ...authContext.user,
          organization: {
            ...authContext.user.organization,
            eventRecordingSettings: data.settings,
          },
        },
        () => {},
      )

      setSaved(true)
      setTimeout(() => setSaved(false), 5000)
    } catch (err) {
      setError(
        err.response?.data?.message ||
          'Failed to update incident recording settings',
      )
    } finally {
      setLoading(false)
    }
  }

  const handleToggle = field => checked => {
    setSettings({ ...settings, [field]: checked })
  }

  return (
    <div className="rounded-lg border bg-card p-4">
      <h6 className="text-base font-semibold mb-1">Incident Recording</h6>
      <p className="text-sm text-muted-foreground mb-4">
        Control whether events are recorded during live incidents. These
        settings do not affect exercises, which always record events.
      </p>

      {saved && (
        <Alert className="mb-2 border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-200">
          <CheckCircle2 className="size-4" />
          <AlertDescription>Incident recording settings saved</AlertDescription>
        </Alert>
      )}

      {error && (
        <Alert variant="destructive" className="mb-2">
          <AlertCircle className="size-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="grid gap-4">
        <div>
          <div className="flex items-center gap-2">
            <Switch
              checked={settings.enabled}
              onCheckedChange={handleToggle('enabled')}
              disabled={loading}
              id="record-events"
            />
            <Label htmlFor="record-events">Record Events</Label>
          </div>
          <p className="text-xs text-muted-foreground ml-10">
            Master switch to enable or disable all event recording during live
            incidents
          </p>
        </div>

        <div>
          <div className="flex items-center gap-2">
            <Switch
              checked={settings.forceOrgSettings}
              onCheckedChange={handleToggle('forceOrgSettings')}
              disabled={loading}
              id="force-org-settings"
            />
            <Label htmlFor="force-org-settings">
              Force Organization Settings
            </Label>
          </div>
          <p className="text-xs text-muted-foreground ml-10">
            When enabled, incident commanders cannot override these settings at
            incident level
          </p>
        </div>

        <div>
          <Separator className="my-1" />
          <p className="text-sm font-medium mb-1">Event Sources</p>
        </div>

        <div>
          <div className="flex items-center gap-2">
            <Switch
              checked={settings.meetingEvents}
              onCheckedChange={handleToggle('meetingEvents')}
              disabled={loading || !settings.enabled}
              id="meeting-events"
            />
            <Label htmlFor="meeting-events">Meeting Events</Label>
          </div>
          <p className="text-xs text-muted-foreground ml-10">
            Record events extracted from meeting transcripts (observations,
            decisions, actions)
          </p>
        </div>

        <div>
          <div className="flex items-center gap-2">
            <Switch
              checked={settings.slackEvents}
              onCheckedChange={handleToggle('slackEvents')}
              disabled={loading || !settings.enabled}
              id="slack-events"
            />
            <Label htmlFor="slack-events">Slack Events</Label>
          </div>
          <p className="text-xs text-muted-foreground ml-10">
            Record events classified from Slack channel messages linked to
            incidents
          </p>
        </div>

        <div>
          <div className="flex items-center gap-2">
            <Switch
              checked={settings.integrationEvents}
              onCheckedChange={handleToggle('integrationEvents')}
              disabled={loading || !settings.enabled}
              id="integration-events"
            />
            <Label htmlFor="integration-events">Integration Events</Label>
          </div>
          <p className="text-xs text-muted-foreground ml-10">
            Record events from external integrations and webhooks
          </p>
        </div>

        <div>
          <Button onClick={handleSave} disabled={loading}>
            {loading ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>
    </div>
  )
}
